import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Eye, Loader2, ShieldAlert, Trash2, Clock, Mail, BellRing, ScrollText, Users, Award, Trophy, CalendarDays, ClipboardList, ArrowLeftRight, CheckCircle2, XCircle, Download } from "lucide-react";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import { ROLE_LABEL } from "@/lib/nav";
import { shortNo } from "@/lib/caseNo";
import DashboardNotificationsCard from "@/components/notifications/DashboardNotificationsCard";

const fmtDT = (v) => v ? new Date(v).toLocaleString("en-US", { timeZone: "America/Chicago", year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" }) : "—";
const fmtDate = (v) => {
  if (!v) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v));
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  return new Date(v).toLocaleDateString("en-US", { timeZone: "America/Chicago", year: "numeric", month: "short", day: "numeric" });
};

function windowCountdown(expiresAt) {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return { label: "expired", urgent: true, days: 0 };
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const label = days >= 1 ? `${days}d ${hours}h left` : `${hours}h left`;
  return { label, urgent: days < 1, days };
}

// ---------------- Dashboard ----------------
const RANGES = [{ key: "7", label: "7 Days" }, { key: "30", label: "30 Days" }, { key: "90", label: "90 Days" }];

const ROLE_REQ_STATUS = { pending: "bg-amber-100 text-amber-700", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700" };

function StaffRoleRequests({ user, campuses }) {
  const isDistrict = user.role === "district_administrator";
  const [items, setItems] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ action: "add", name: "", email: "", campus_id: "", target: "" });
  const load = useCallback(() => api.get("/role-requests").then(({ data }) => setItems(data.items || [])).catch(() => {}), []);
  useEffect(() => { load(); api.get("/staff/users").then(({ data }) => setCoaches((data.items || []).filter((u) => u.role === "coach"))).catch(() => {}); }, [load]);

  const canSubmit = form.action === "add"
    ? (!!form.email && (!isDistrict || !!form.campus_id))
    : !!form.target;

  const submit = async () => {
    setBusy(true);
    try {
      let body;
      if (form.action === "add") {
        body = { action: "add", role: "campus_administrator", name: form.name || null, email: form.email };
        if (isDistrict) body.campus_id = form.campus_id;
      } else {
        const t = coaches.find((c) => c.id === form.target);
        if (!t) { toast.error("Select a coach"); setBusy(false); return; }
        body = { action: "deactivate", role: "coach", target_user_id: t.id,
                 name: `${t.first_name || ""} ${t.last_name || ""}`.trim(), email: t.email };
      }
      await api.post("/role-requests", body);
      toast.success("Request submitted to System Administrator");
      setOpen(false); setForm({ action: "add", name: "", email: "", campus_id: "", target: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not submit request"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="mt-6 border-slate-200 p-6 shadow-sm" data-testid="staff-role-request-card">
      <div className="mb-3 flex items-center gap-2">
        <ArrowLeftRight className="h-5 w-5 text-violet-600" />
        <h3 className="text-lg font-semibold text-slate-800 font-heading">Role change requests</h3>
        <Button size="sm" className="ml-auto rounded-full bg-slate-900 hover:bg-slate-800" onClick={() => setOpen(true)} data-testid="staff-role-request-btn">Request a role change</Button>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="staff-role-request-empty">No role change requests yet.</p>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={`staff-role-request-${r.id}`}>
              <div>
                <p className="text-sm font-semibold text-slate-800"><span className="capitalize">{r.action}</span> {ROLE_LABEL[r.role] || r.role} <span className="font-normal text-slate-400">— {r.target_name || r.target_email || "existing account"}</span></p>
                <p className="text-xs text-slate-500">By {r.requested_by_name} · {fmtDate(r.created_at)}</p>
                {r.decline_reason && <p className="mt-0.5 text-xs text-red-600" data-testid={`staff-role-request-reason-${r.id}`}>Reason: “{r.decline_reason}”</p>}
              </div>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${ROLE_REQ_STATUS[r.status] || "bg-slate-100 text-slate-600"}`} data-testid={`staff-role-request-status-${r.id}`}>{r.status}</span>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="staff-role-request-dialog" className="max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Request a role change</DialogTitle>
            <DialogDescription>Submitted to the System Administrator for approval.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Action</Label>
              <Select value={form.action} onValueChange={(v) => setForm((f) => ({ ...f, action: v }))}>
                <SelectTrigger data-testid="staff-role-request-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">Add a Campus Administrator</SelectItem>
                  <SelectItem value="deactivate">Deactivate a Coach</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.action === "add" ? (
              <>
                {isDistrict && (
                  <div className="space-y-2">
                    <Label>Campus</Label>
                    <Select value={form.campus_id} onValueChange={(v) => setForm((f) => ({ ...f, campus_id: v }))}>
                      <SelectTrigger data-testid="staff-role-request-campus"><SelectValue placeholder="Select a campus" /></SelectTrigger>
                      <SelectContent>
                        {campuses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2"><Label>Name (optional)</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="staff-role-request-name" /></div>
                <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="staff-role-request-email" /></div>
                <p className="text-xs text-slate-500">On approval, an invite is emailed so the administrator gets instant-access oversight.</p>
              </>
            ) : (
              <div className="space-y-2">
                <Label>Coach to deactivate</Label>
                {coaches.length === 0 ? (
                  <p className="text-sm text-slate-400" data-testid="staff-role-request-no-coaches">No active coaches in your scope.</p>
                ) : (
                  <Select value={form.target} onValueChange={(v) => setForm((f) => ({ ...f, target: v }))}>
                    <SelectTrigger data-testid="staff-role-request-target"><SelectValue placeholder="Select a coach" /></SelectTrigger>
                    <SelectContent>
                      {coaches.map((c) => <SelectItem key={c.id} value={c.id}>{`${c.first_name || ""} ${c.last_name || ""}`.trim()} · {c.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-full bg-slate-900 hover:bg-slate-800" disabled={busy || !canSubmit} onClick={submit} data-testid="staff-role-request-submit">Submit request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export function StaffDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [requests, setRequests] = useState([]);
  const [rangeKey, setRangeKey] = useState("30");
  const [analytics, setAnalytics] = useState(null);
  const [scopeOpts, setScopeOpts] = useState({ campuses: [], teams: [] });
  const [campusFilter, setCampusFilter] = useState("all");
  const [teamFilter, setTeamFilter] = useState("all");
  const [, forceTick] = useState(0);

  const loadDash = useCallback(() => api.get("/staff/dashboard").then(({ data }) => setD(data)).catch(() => {}), []);
  const loadReq = useCallback(() => api.get("/staff/deletion-requests").then(({ data }) => setRequests(data.items || [])).catch(() => {}), []);
  useEffect(() => { loadDash(); loadReq(); }, [loadDash, loadReq]);
  useEffect(() => { api.get("/staff/scope-options").then(({ data }) => setScopeOpts({ campuses: data.campuses || [], teams: data.teams || [] })).catch(() => {}); }, []);
  useEffect(() => {
    setAnalytics(null);
    const p = new URLSearchParams({ days: rangeKey });
    if (campusFilter !== "all") p.set("campus_id", campusFilter);
    if (teamFilter !== "all") p.set("team_id", teamFilter);
    api.get(`/staff/analytics?${p.toString()}`).then(({ data }) => setAnalytics(data)).catch(() => {});
  }, [rangeKey, campusFilter, teamFilter]);
  useEffect(() => { const t = setInterval(() => forceTick((x) => x + 1), 60000); return () => clearInterval(t); }, []);

  const ACTIVE = ["pending", "awaiting_activation", "window_active", "window_expired", "coach_pending", "coach_requested"];
  const activeReqs = requests.filter((r) => ACTIVE.includes(r.status));
  const pending = requests.filter((r) => r.status === "pending");
  const n = analytics?.totals;
  const fmtDay = (s) => { const [, m, day] = s.split("-"); return `${parseInt(m)}/${parseInt(day)}`; };

  const handleReq = async (rid, action) => {
    try {
      await api.post(`/staff/deletion-requests/${rid}/${action}`);
      toast.success(action === "approve" ? "Approved — System Administrator asked to activate your delete options" : "Request declined");
      loadReq(); loadDash();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const StatCard = ({ label, value, icon: Icon, accent, onClick, testId }) => (
    <Card onClick={onClick} data-testid={testId}
      className={`border-slate-200 p-5 shadow-sm ${onClick ? "cursor-pointer transition-shadow hover:shadow-md hover:border-slate-300" : ""}`}>
      <div className="flex items-center gap-3">
        <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${accent}`}><Icon className="h-5 w-5" /></span>
        <div>
          <p className="font-heading text-3xl font-extrabold tabular-nums text-slate-900">{value ?? "—"}</p>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
        </div>
      </div>
    </Card>
  );

  return (
    <div>
      <PageHeader title="Administration" subtitle={d ? `${ROLE_LABEL[user.role]} · ${d.district_name || ""}${d.campus_name ? " / " + d.campus_name : ""}` : "Loading…"} />

      {/* Active delete-window countdown */}
      {(d?.active_windows || []).length > 0 && (
        <div className="mb-6 space-y-2" data-testid="delete-window-banner">
          {d.active_windows.map((w) => {
            const cd = windowCountdown(w.window_expires_at);
            return (
              <div key={w.id} className={`flex flex-wrap items-center gap-2 rounded-xl border p-4 ${cd?.urgent ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`} data-testid={`delete-window-${w.id}`}>
                <Clock className={`h-5 w-5 ${cd?.urgent ? "text-red-600" : "text-amber-600"}`} />
                <span className="text-sm font-semibold text-slate-800">Delete options active for {w.requester_name}</span>
                <span className={`ml-auto rounded-full px-3 py-1 text-xs font-bold ${cd?.urgent ? "bg-red-600 text-white" : "bg-amber-500 text-white"}`} data-testid={`delete-window-countdown-${w.id}`}>{cd?.label}</span>
                <span className="w-full text-xs text-slate-500">Go to <strong>Users</strong> to permanently delete this account before the window closes on {fmtDate(w.window_expires_at)}.</span>
              </div>
            );
          })}
        </div>
      )}

      {(d?.teams_without_coach || []).length > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="teams-without-coach-banner">
          <ShieldAlert className="h-5 w-5 text-amber-600" />
          <span className="text-sm font-semibold text-slate-800">These teams have no active coach — add one:</span>
          <span className="text-sm text-slate-600">{d.teams_without_coach.map((t) => t.name).join(", ")}</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Coaches" value={d?.coaches} icon={Users} accent="bg-sky-50 text-sky-600" testId="dashboard-card-coaches" onClick={() => navigate("/staff/users?role=coach")} />
      </div>

      {/* Deletion request queue */}
      <Card className="mt-6 border-slate-200 p-6 shadow-sm" data-testid="deletion-requests-card">
        <div className="mb-3 flex items-center gap-2">
          <ScrollText className="h-5 w-5 text-slate-400" />
          <h3 className="text-lg font-semibold text-slate-800 font-heading">Data deletion requests</h3>
          {pending.length > 0 && <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-700" data-testid="pending-deletion-count">{pending.length} pending</span>}
        </div>
        {requests.length === 0 ? <p className="text-sm text-slate-400" data-testid="no-deletion-requests">No deletion requests.</p> : activeReqs.length === 0 ? <p className="text-sm text-slate-400" data-testid="no-active-deletion-requests">No open requests.</p> : (
          <div className="space-y-3">
            {activeReqs.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={`deletion-request-${r.id}`}>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{r.requester_name} <span className="font-normal text-slate-400">({ROLE_LABEL[r.requester_role]})</span></p>
                  <p className="text-xs text-slate-500">{r.requester_email} · requested {fmtDT(r.created_at)}</p>
                  {r.reason && <p className="mt-1 text-xs italic text-slate-500">"{r.reason}"</p>}
                </div>
                {r.status === "pending" ? (
                  <div className="flex gap-2">
                    <Button size="sm" className="rounded-full bg-slate-900 hover:bg-slate-800" onClick={() => handleReq(r.id, "approve")} data-testid={`deletion-approve-${r.id}`}>Approve</Button>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => handleReq(r.id, "deny")} data-testid={`deletion-deny-${r.id}`}>Decline</Button>
                  </div>
                ) : (
                  <StatusPill status={r.status} testId={`deletion-status-${r.id}`} />
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <StaffRoleRequests user={user} campuses={scopeOpts.campuses} />

      {/* Scoped notification analytics */}
      <Card className="mt-6 border-slate-200 p-5 shadow-sm" data-testid="staff-analytics">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <BellRing className="h-4 w-4 text-slate-500" />
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Notifications Sent</p>
          {user.role === "district_administrator" && (
            <Select value={campusFilter} onValueChange={(v) => { setCampusFilter(v); setTeamFilter("all"); }}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="analytics-campus-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All campuses</SelectItem>
                {scopeOpts.campuses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={teamFilter} onValueChange={setTeamFilter}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="analytics-team-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All teams</SelectItem>
              {scopeOpts.teams
                .filter((t) => campusFilter === "all" || t.campus_id === campusFilter)
                .map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="ml-auto flex gap-1 rounded-full bg-slate-100 p-0.5" data-testid="analytics-range-toggle">
            {RANGES.map((r) => (
              <button key={r.key} onClick={() => setRangeKey(r.key)} data-testid={`analytics-range-${r.key}`}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${rangeKey === r.key ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}>{r.label}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div data-testid="notif-total"><p className="font-heading text-3xl font-extrabold tabular-nums text-slate-900">{n?.total ?? "—"}</p><p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Total</p></div>
          <div data-testid="notif-email"><p className="flex items-center gap-1.5 font-heading text-3xl font-extrabold tabular-nums text-sky-600"><Mail className="h-5 w-5" />{n?.email ?? "—"}</p><p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Email</p></div>
          <div data-testid="notif-push"><p className="flex items-center gap-1.5 font-heading text-3xl font-extrabold tabular-nums text-violet-600"><BellRing className="h-5 w-5" />{n?.push ?? "—"}</p><p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Push</p></div>
          <div data-testid="notif-sms"><p className="font-heading text-3xl font-extrabold tabular-nums text-emerald-600">{n?.sms ?? "—"}</p><p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-slate-400">SMS</p></div>
        </div>
        <div className="mt-5 w-full" data-testid="analytics-chart">
          {analytics && (
            <ResponsiveContainer width="100%" height={176}>
              <BarChart data={analytics.series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: "#94a3b8" }} interval="preserveStartEnd" minTickGap={24} axisLine={false} tickLine={false} />
                <Tooltip labelFormatter={fmtDay} cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Bar dataKey="email" stackId="a" fill="#0284c7" name="Email" />
                <Bar dataKey="push" stackId="a" fill="#7c3aed" name="Push" />
                <Bar dataKey="sms" stackId="a" fill="#059669" name="SMS" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>
      <DashboardNotificationsCard />
    </div>
  );
}

function StatusPill({ status, testId }) {
  const map = {
    pending: ["bg-amber-100 text-amber-700", "Pending"],
    awaiting_activation: ["bg-sky-100 text-sky-700", "Approved · awaiting activation"],
    window_active: ["bg-indigo-100 text-indigo-700", "Delete window active"],
    window_expired: ["bg-slate-100 text-slate-600", "Window expired"],
    coach_requested: ["bg-sky-100 text-sky-700", "Sent to System Admin"],
    completed: ["bg-emerald-100 text-emerald-700", "Completed"],
    closed_no_deletion: ["bg-slate-100 text-slate-600", "Closed — no deletion"],
    denied: ["bg-red-100 text-red-700", "Declined"],
  };
  const [cls, label] = map[status] || ["bg-slate-100 text-slate-600", status];
  return <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`} data-testid={testId}>{label}</span>;
}

// ---------------- Users (read-only) ----------------
export function StaffUsers() {
  const { user, viewAsCoach } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [windows, setWindows] = useState([]);
  const [scopeOpts, setScopeOpts] = useState({ campuses: [], teams: [] });
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [busyView, setBusyView] = useState("");
  const [delTarget, setDelTarget] = useState(null);

  const roleFilter = searchParams.get("role") || "all";
  const campusFilter = searchParams.get("campus") || "all";
  const teamFilter = searchParams.get("team") || "all";
  const setParam = (key, val) => {
    const p = new URLSearchParams(searchParams);
    if (val && val !== "all") p.set(key, val); else p.delete(key);
    if (key === "campus") p.delete("team"); // reset team when campus changes
    setSearchParams(p, { replace: true });
  };

  const loadUsers = useCallback(() => api.get("/staff/users").then(({ data }) => setItems(data.items || [])).catch(() => {}), []);
  const loadWindows = useCallback(() => api.get("/staff/delete-windows").then(({ data }) => setWindows(data.items || [])).catch(() => {}), []);
  useEffect(() => { loadUsers(); loadWindows(); }, [loadUsers, loadWindows]);
  useEffect(() => { api.get("/staff/scope-options").then(({ data }) => setScopeOpts({ campuses: data.campuses || [], teams: data.teams || [] })).catch(() => {}); }, []);

  const winByRequester = {};
  windows.forEach((w) => { winByRequester[w.requester_id] = w; });

  const campusOnlyCoaches = user.role === "campus_administrator" || user.role === "district_administrator";
  const shown = items.filter((u) => {
    if (campusOnlyCoaches && u.role !== "coach") return false;
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    if (campusFilter !== "all" && u.campus_id !== campusFilter) return false;
    if (teamFilter !== "all" && u.team_id !== teamFilter) return false;
    const s = q.trim().toLowerCase();
    return !s || [u.first_name, u.last_name, u.email, u.team_name, u.campus_name].some((v) => (v || "").toLowerCase().includes(s));
  });

  const openCoach = async (c) => {
    setDetail(c); setDetailData(null);
    try { const { data } = await api.get(`/staff/coach/${c.id}`); setDetailData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const viewDash = async (id) => {
    setBusyView(id);
    try { await viewAsCoach(id); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusyView(""); }
  };
  const execDelete = async () => {
    const win = winByRequester[delTarget.id];
    if (!win) return;
    try {
      await api.post(`/staff/deletion-requests/${win.id}/execute`);
      toast.success(`Permanently deleted ${delTarget.first_name} ${delTarget.last_name}`);
      setDelTarget(null); loadUsers(); loadWindows();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Users" subtitle={campusOnlyCoaches ? "Coaches in your scope. View-only oversight of coach activity and dashboards." : "Coaches, members and parents in your scope. View-only — deletion requires an active window from the System Administrator."} />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, team…" className="pl-9" data-testid="users-search" />
        </div>
        {!campusOnlyCoaches && (
          <Select value={roleFilter} onValueChange={(v) => setParam("role", v)}>
            <SelectTrigger className="w-40" data-testid="users-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="coach">Coaches</SelectItem>
              <SelectItem value="team_member">Team Members</SelectItem>
              <SelectItem value="parent">Parents</SelectItem>
            </SelectContent>
          </Select>
        )}
        {user.role === "district_administrator" && (
          <Select value={campusFilter} onValueChange={(v) => setParam("campus", v)}>
            <SelectTrigger className="w-40" data-testid="users-campus-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All campuses</SelectItem>
              {scopeOpts.campuses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={teamFilter} onValueChange={(v) => setParam("team", v)}>
          <SelectTrigger className="w-40" data-testid="users-team-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All teams</SelectItem>
            {scopeOpts.teams
              .filter((t) => campusFilter === "all" || t.campus_id === campusFilter)
              .map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {shown.length === 0 ? <EmptyState title="No accounts" description="No accounts match your filters." testId="staff-users-empty" /> : (
        <Card className="border-slate-200 shadow-sm overflow-x-auto">
          <Table data-testid="staff-users-table">
            <TableHeader><TableRow className="bg-slate-50">{["Name", "Role", "Team / Campus", "Status", "Actions"].map((h) => <TableHead key={h} className="text-xs font-bold uppercase text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {shown.map((u) => {
                const win = winByRequester[u.id];
                const canDelete = !!win && (u.role === "team_member" || u.role === "parent");
                const inactive = u.is_active === false;
                const statusLabel = inactive ? "Inactive" : (u.assignment_status && u.assignment_status !== "approved" ? u.assignment_status : "Active");
                const statusCls = inactive ? "bg-slate-100 text-slate-500"
                  : u.assignment_status === "pending" ? "bg-amber-100 text-amber-700"
                  : u.assignment_status === "rejected" ? "bg-red-100 text-red-700"
                  : "bg-emerald-100 text-emerald-700";
                return (
                  <TableRow key={u.id} data-testid={`staff-user-${u.id}`} className={inactive ? "opacity-50" : ""}>
                    <TableCell><span className="font-semibold text-slate-800">{u.last_name}, {u.first_name}</span><span className="block text-xs text-slate-400">{u.email}</span></TableCell>
                    <TableCell><span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">{ROLE_LABEL[u.role]}</span></TableCell>
                    <TableCell className="text-sm text-slate-500">{[u.team_name, u.campus_name].filter(Boolean).join(" · ") || "—"}</TableCell>
                    <TableCell><span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${statusCls}`}>{statusLabel}</span></TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        {u.role === "coach" ? (
                          <>
                            {!campusOnlyCoaches && (
                              <Button size="sm" variant="outline" className="rounded-full" onClick={() => openCoach(u)} data-testid={`user-view-activity-${u.id}`}><Eye className="mr-1.5 h-3.5 w-3.5" />Activity</Button>
                            )}
                            <Button size="sm" variant="ghost" className="rounded-full text-slate-500" disabled={!!busyView} onClick={() => viewDash(u.id)} data-testid={`user-view-dashboard-${u.id}`}>{busyView === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "View dashboard"}</Button>
                          </>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline"
                              className={`rounded-full ${canDelete ? "border-red-300 text-red-600 hover:bg-red-50" : "cursor-not-allowed border-slate-200 text-slate-300"}`}
                              disabled={!canDelete} onClick={() => canDelete && setDelTarget(u)}
                              data-testid={`user-delete-${u.id}`} title={canDelete ? "Delete window active" : "Delete disabled — requires an approved request + System Administrator activation"}>
                              <Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete
                            </Button>
                            {win && <span className="text-[11px] font-semibold text-amber-600" data-testid={`user-window-${u.id}`}>{windowCountdown(win.window_expires_at)?.label}</span>}
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Coach activity drill-down */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[90dvh] max-w-3xl overflow-y-auto" data-testid="coach-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{detail?.display_name || `Coach ${detail?.last_name}`} · Activity</DialogTitle>
            <DialogDescription>{detail?.team_name || "—"}{detail?.campus_name ? ` · ${detail.campus_name}` : ""} — read-only</DialogDescription>
          </DialogHeader>
          {!detailData ? <div className="flex items-center gap-2 py-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading activity…</div> : (
            <Tabs defaultValue="merits" className="w-full">
              <TabsList className="flex flex-wrap">
                <TabsTrigger value="merits" data-testid="coach-tab-merits"><Award className="mr-1.5 h-3.5 w-3.5" />Merits ({detailData.merits.length})</TabsTrigger>
                <TabsTrigger value="lettering" data-testid="coach-tab-lettering"><Trophy className="mr-1.5 h-3.5 w-3.5" />Lettering ({detailData.lettering.length})</TabsTrigger>
                <TabsTrigger value="roster" data-testid="coach-tab-roster"><Users className="mr-1.5 h-3.5 w-3.5" />Roster ({detailData.roster.length})</TabsTrigger>
                <TabsTrigger value="events" data-testid="coach-tab-events"><CalendarDays className="mr-1.5 h-3.5 w-3.5" />Events ({detailData.events.length})</TabsTrigger>
                <TabsTrigger value="audit" data-testid="coach-tab-audit"><ScrollText className="mr-1.5 h-3.5 w-3.5" />Audit ({detailData.audit.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="merits" className="mt-3">
                <DetailList empty="No merit/demerit cases." items={detailData.merits} render={(m) => (
                  <div key={m.id} className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`coach-merit-${m.id}`}>
                    <div><p className="font-semibold text-slate-800">{m.member_name} <span className="font-normal text-slate-400">· {shortNo(m.case_number)}</span></p><p className="text-xs text-slate-500">{m.description || m.category} · {fmtDate(m.date_issued)}</p></div>
                    <span className={`shrink-0 font-bold tabular-nums ${(m.value || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{(m.value || 0) > 0 ? "+" : ""}{m.value}</span>
                  </div>
                )} />
              </TabsContent>
              <TabsContent value="lettering" className="mt-3">
                <DetailList empty="No lettering submissions." items={detailData.lettering} render={(s) => (
                  <div key={s.id} className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`coach-lettering-${s.id}`}>
                    <div><p className="font-semibold text-slate-800">{s.member_name}</p><p className="text-xs text-slate-500">{s.description || s.category} · {fmtDate(s.date_submitted)} · <span className="capitalize">{s.status}</span></p></div>
                    <span className="shrink-0 font-bold tabular-nums text-slate-700">{s.points_requested_snapshot ?? s.points ?? "—"} pts</span>
                  </div>
                )} />
              </TabsContent>
              <TabsContent value="roster" className="mt-3">
                <DetailList empty="No members on this team." items={detailData.roster} render={(r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`coach-roster-${r.id}`}>
                    <div><p className="font-semibold text-slate-800">{r.last_name}, {r.first_name}</p><p className="text-xs text-slate-500">{r.email}{r.graduation_year ? ` · Class of ${r.graduation_year}` : ""}</p></div>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${r.is_active === false ? "bg-slate-100 text-slate-500" : "bg-emerald-100 text-emerald-700"}`}>{r.assignment_status || (r.is_active === false ? "inactive" : "active")}</span>
                  </div>
                )} />
              </TabsContent>
              <TabsContent value="events" className="mt-3">
                <DetailList empty="No events created." items={detailData.events} render={(e) => (
                  <div key={e.id} className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`coach-event-${e.id}`}>
                    <div><p className="font-semibold text-slate-800">{e.title}</p><p className="text-xs text-slate-500">{fmtDate(e.event_date)}{e.start_time ? ` · ${e.start_time}` : ""}{e.location ? ` · ${e.location}` : ""}</p></div>
                  </div>
                )} />
              </TabsContent>
              <TabsContent value="audit" className="mt-3">
                <DetailList empty="No audit activity." items={detailData.audit} render={(a) => (
                  <div key={a.id} className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`coach-audit-${a.id}`}>
                    <div><p className="font-semibold text-slate-800">{a.change_summary || a.action_type}</p><p className="text-xs text-slate-400">{a.action_type}</p></div>
                    <span className="shrink-0 text-xs text-slate-400">{fmtDT(a.timestamp)}</span>
                  </div>
                )} />
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent data-testid="user-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">Permanently delete account</DialogTitle>
            <DialogDescription>Delete <strong>{delTarget?.first_name} {delTarget?.last_name}</strong> ({delTarget && ROLE_LABEL[delTarget.role]}) and all of their records. A final confirmation is emailed to them first. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDelTarget(null)} data-testid="user-delete-cancel">Cancel</Button>
            <Button className="rounded-full bg-red-600 hover:bg-red-700" onClick={execDelete} data-testid="user-delete-confirm">Permanently delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailList({ items, render, empty }) {
  if (!items || items.length === 0) return <p className="py-4 text-sm text-slate-400">{empty}</p>;
  return <div className="max-h-[55vh] divide-y divide-slate-100 overflow-y-auto">{items.map(render)}</div>;
}

// ---------------- Programs (read-only) ----------------
export function StaffPrograms() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ teams: [], merit_presets: [] });
  const [history, setHistory] = useState([]);
  useEffect(() => { api.get("/staff/programs").then(({ data }) => setData(data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/staff/deletion-requests")
      .then(({ data }) => setHistory((data.items || []).filter((r) => ["completed", "denied"].includes(r.status))))
      .catch(() => {});
  }, []);
  const openTeam = (t) => {
    const p = new URLSearchParams({ team: t.id });
    if (user.role === "district_administrator" && t.campus_id) p.set("campus", t.campus_id);
    navigate(`/staff/users?${p.toString()}`);
  };
  return (
    <div>
      <PageHeader title="Programs" subtitle="Merit & lettering configuration for teams in your scope (read-only)." />
      <Card className="mb-4 border-slate-200 p-6 shadow-sm" data-testid="staff-programs-teams">
        <h3 className="mb-3 text-lg font-semibold text-slate-800 font-heading">Teams ({data.teams.length})</h3>
        <div className="flex flex-wrap gap-2">
          {data.teams.length === 0 ? <span className="text-sm text-slate-400">No teams in scope.</span> :
            data.teams.map((t) => (
              <button key={t.id} onClick={() => openTeam(t)} data-testid={`program-team-${t.id}`}
                className="rounded-full bg-sky-100 px-3 py-1 text-sm font-semibold text-sky-700 transition-colors hover:bg-sky-200">
                {t.name}{t.campus_name ? ` · ${t.campus_name}` : ""}
              </button>
            ))}
        </div>
      </Card>
      <Card className="border-slate-200 p-6 shadow-sm" data-testid="staff-programs-presets">
        <h3 className="mb-3 text-lg font-semibold text-slate-800 font-heading">Merit presets ({data.merit_presets.length})</h3>
        {data.merit_presets.length === 0 ? <span className="text-sm text-slate-400">No merit presets in scope.</span> : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {data.merit_presets.map((p) => (
              <div key={p.id} className="rounded-lg border border-slate-100 p-3 text-sm">
                <p className="font-semibold text-slate-800">{p.name}</p>
                <p className="text-xs capitalize text-slate-500">{p.category} · {p.value_type === "range" ? `${p.min}–${p.max} pts` : `${p.value} pts`}</p>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Decision history (read-only) — moved from the Dashboard */}
      {history.length > 0 && (
        <Card className="mt-4 border-slate-200 p-6 shadow-sm" data-testid="deletion-history-card">
          <div className="mb-3 flex items-center gap-2">
            <ScrollText className="h-5 w-5 text-slate-400" />
            <h3 className="text-lg font-semibold text-slate-800 font-heading">Decision history</h3>
            <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-bold text-slate-600">{history.length}</span>
          </div>
          <div className="divide-y divide-slate-100">
            {history.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 py-2.5" data-testid={`deletion-history-${r.id}`}>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{r.requester_name} <span className="font-normal text-slate-400">({ROLE_LABEL[r.requester_role]})</span></p>
                  <p className="text-xs text-slate-500">{r.requester_email} · decided {fmtDate(r.completed_at || r.handled_at)}</p>
                  {r.decline_reason && <p className="mt-0.5 text-xs text-red-600" data-testid={`deletion-history-reason-${r.id}`}>Reason: “{r.decline_reason}”</p>}
                </div>
                <StatusPill status={r.deactivated && r.status === "completed" ? "closed_no_deletion" : r.status} testId={`deletion-history-status-${r.id}`} />
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}


export function StaffEmailAudit() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 30 });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: String(page), page_size: "30" });
    if (q.trim()) params.set("q", q.trim());
    const { data } = await api.get(`/staff/email-audit?${params.toString()}`);
    setData(data);
  }, [page, q]);
  useEffect(() => { const t = setTimeout(load, q ? 350 : 0); return () => clearTimeout(t); }, [load, q]);

  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));

  return (
    <div>
      <PageHeader title="Email Audit" subtitle="Every email sent within your domain — who initiated it, the recipient, and when." />
      <Card className="mb-4 border-slate-200 p-4 shadow-sm">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search recipient or subject"
            className="pl-9" data-testid="email-audit-search" />
        </div>
      </Card>
      {data.items.length === 0 ? (
        <EmptyState title="No emails yet" description="Emails sent to people in your domain will be listed here." testId="email-audit-empty" />
      ) : (
        <Card className="border-slate-200 shadow-sm overflow-x-auto" data-testid="email-audit-card">
          <Table data-testid="email-audit-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                {["Initiated by", "Recipient", "Email address", "Subject", "Sent", "Status"].map((h) => (
                  <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((a) => (
                <TableRow key={a.id} data-testid={`email-audit-row-${a.id}`}>
                  <TableCell className="text-sm font-medium text-slate-800">{a.email_initiator || a.acting_user_name || "System"}</TableCell>
                  <TableCell className="text-sm text-slate-700" data-testid={`email-audit-recipient-${a.id}`}>{a.email_recipient_name || "—"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{a.email_recipient || "—"}</TableCell>
                  <TableCell className="max-w-xs text-sm text-slate-600">{a.email_subject || a.change_summary}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-slate-500">{fmtDT(a.timestamp)}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${a.email_status === "failed" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`} data-testid={`email-audit-status-${a.id}`}>
                      {a.email_status === "failed" ? <XCircle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                      {a.email_status === "failed" ? "Failed" : "Delivered"}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      <div className="mt-4 flex items-center justify-between">
        <span className="text-xs text-slate-500" data-testid="email-audit-page-label">Page {data.page} of {totalPages} · {data.total} emails</span>
        {totalPages > 1 && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-full" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="email-audit-prev">Prev</Button>
            <Button variant="outline" size="sm" className="rounded-full" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="email-audit-next">Next</Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- Deletion Log ----------------
const SCOPE_LABEL = { self: "Self-requested account", individual: "Individual account", team: "Team", campus: "Campus", district: "District" };

export function StaffDeletionLog() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 30 });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: String(page), page_size: "30" });
    if (q.trim()) params.set("q", q.trim());
    const { data } = await api.get(`/staff/deletion-log?${params.toString()}`);
    setData(data);
  }, [page, q]);
  useEffect(() => { const t = setTimeout(load, q ? 350 : 0); return () => clearTimeout(t); }, [load, q]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      const res = await api.get(`/staff/deletion-log/export?${params.toString()}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = "deletion_log.csv";
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Deletion log exported");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExporting(false); }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));

  return (
    <div>
      <PageHeader title="Deletion Log" subtitle="Every completed data-deletion within your domain — who requested it, who approved it, what was deleted, and when." />
      <Card className="mb-4 border-slate-200 p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search requester, approver or item"
              className="pl-9" data-testid="deletion-log-search" />
          </div>
          <Button variant="outline" className="rounded-full" onClick={exportCsv} disabled={exporting || data.total === 0} data-testid="deletion-log-export-csv">
            {exporting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Export CSV
          </Button>
        </div>
      </Card>
      {data.items.length === 0 ? (
        <EmptyState title="No deletions yet" description="Completed deletion actions within your domain will be listed here." testId="deletion-log-empty" />
      ) : (
        <Card className="border-slate-200 shadow-sm overflow-x-auto" data-testid="deletion-log-card">
          <Table data-testid="deletion-log-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                {["Requested by", "Approved by", "What was deleted", "Executed", "Accounts / records"].map((h) => (
                  <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((r) => (
                <TableRow key={r.id} data-testid={`deletion-log-row-${r.id}`}>
                  <TableCell className="text-sm text-slate-800" data-testid={`deletion-log-requester-${r.id}`}>
                    <div className="font-medium">{r.requester_name || "—"}</div>
                    {r.requester_email && <div className="text-xs text-slate-500">{r.requester_email}</div>}
                  </TableCell>
                  <TableCell className="text-sm text-slate-700" data-testid={`deletion-log-approver-${r.id}`}>
                    <div className="font-medium">{r.approver_name || r.actor_name || "—"}</div>
                    {(r.approver_role || r.actor_role) && <div className="text-xs text-slate-500">{ROLE_LABEL[r.approver_role || r.actor_role] || (r.approver_role || r.actor_role)}</div>}
                  </TableCell>
                  <TableCell className="max-w-xs text-sm text-slate-600">
                    <div>{r.target_desc || "—"}</div>
                    <span className="mt-0.5 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{SCOPE_LABEL[r.scope] || r.scope}</span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-slate-500">{fmtDT(r.timestamp)}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-slate-700">{r.accounts_deleted} / {r.records_deleted}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      <div className="mt-4 flex items-center justify-between">
        <span className="text-xs text-slate-500" data-testid="deletion-log-page-label">Page {data.page} of {totalPages} · {data.total} deletions</span>
        {totalPages > 1 && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-full" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="deletion-log-prev">Prev</Button>
            <Button variant="outline" size="sm" className="rounded-full" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="deletion-log-next">Next</Button>
          </div>
        )}
      </div>
    </div>
  );
}



const rbMoney = (n) => { const v = Number(n || 0); return `${v < 0 ? "-" : ""}$${Math.abs(v).toFixed(2)}`; };

function RBStat({ label, value, tone, testid }) {
  return (
    <Card className="border-slate-200 p-4 shadow-sm" data-testid={testid}>
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`mt-1 text-2xl font-extrabold font-heading tabular-nums ${tone}`}>{value}</p>
    </Card>
  );
}

export function StaffRallyBooks() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/staff/oversight/rally-books").then(({ data }) => setData(data)).catch(() => {}); }, []);
  const items = data?.items || [];
  const t = data?.totals || {};
  return (
    <div data-testid="staff-rally-books">
      <PageHeader title="Rally Books Oversight" subtitle="Read-only roll-up of team balances across your scope." />
      <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <RBStat label="Total charged" value={rbMoney(t.owed)} tone="text-slate-800" testid="rb-total-owed" />
        <RBStat label="Total collected" value={rbMoney(t.collected)} tone="text-emerald-600" testid="rb-total-collected" />
        <RBStat label="Credits applied" value={rbMoney(t.credits)} tone="text-blue-600" testid="rb-total-credits" />
        <RBStat label="Outstanding balance" value={rbMoney(t.outstanding)} tone={(t.outstanding || 0) > 0 ? "text-orange-600" : "text-emerald-600"} testid="rb-total-outstanding" />
      </div>
      <Card className="border-slate-200 p-6 shadow-sm">
        <h3 className="mb-3 text-lg font-semibold text-slate-800 font-heading">Team balances ({items.length})</h3>
        {items.length === 0 ? <EmptyState title="No teams in scope" description="Team Rally Books balances will appear here once teams in your scope start using them." testId="rb-empty" /> : (
          <div className="overflow-x-auto">
            <Table className="[&_th]:text-center [&_td]:text-center" data-testid="rb-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Team</TableHead>
                  <TableHead>Campus</TableHead>
                  <TableHead className="text-right">Members</TableHead>
                  <TableHead className="text-right">Charged</TableHead>
                  <TableHead className="text-right">Collected</TableHead>
                  <TableHead className="text-right">Credits</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.team_id} data-testid={`rb-row-${r.team_id}`} className="even:bg-slate-50/60">
                    <TableCell className="font-medium text-slate-800">{r.team_name}</TableCell>
                    <TableCell className="text-slate-500">{r.campus_name || "—"}</TableCell>
                    <TableCell className="text-right text-slate-500">{r.members}</TableCell>
                    <TableCell className="text-right text-slate-700">{rbMoney(r.owed)}</TableCell>
                    <TableCell className="text-right text-emerald-600">{rbMoney(r.collected)}</TableCell>
                    <TableCell className="text-right text-blue-600">{rbMoney(r.credits)}</TableCell>
                    <TableCell className={`text-right font-semibold ${r.outstanding > 0 ? "text-orange-600" : "text-slate-500"}`}>{rbMoney(r.outstanding)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
